import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#050505]" data-testid="privacy-page">
      <header className="sticky top-0 z-50 glass-darker border-b border-white/5">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-heading text-xl font-bold text-white">Privacy Policy</h1>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="prose-invert space-y-6 text-zinc-300 text-sm leading-relaxed">
          <p className="text-zinc-500 text-xs">Last updated: February 2026</p>

          <Section title="1. Who We Are">
            Bitcoin Brain is operated by Bryce Fields, an individual based in California, USA. This policy explains how we collect, use, and protect your personal information.
          </Section>

          <Section title="2. What We Collect">
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong className="text-white">Account info:</strong> Name, email address, password (hashed)</li>
              <li><strong className="text-white">Purchase info:</strong> Tier purchased, payment method (card or Bitcoin), transaction ID. We do not store credit card numbers — Stripe handles that.</li>
              <li><strong className="text-white">Learning data:</strong> Course progress, quiz scores, BTC XP, badges, streaks</li>
              <li><strong className="text-white">Educator data:</strong> Linked student names and emails</li>
              <li><strong className="text-white">Fund a Family applications:</strong> Name, email, household size, written explanation, proof documents (if uploaded)</li>
            </ul>
          </Section>

          <Section title="3. How We Use Your Data">
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>To provide and improve the Bitcoin Brain educational experience</li>
              <li>To process payments via Stripe and Strike</li>
              <li>To send transactional emails (welcome, purchase confirmation, password reset)</li>
              <li>To send content emails (weekly brief, monthly article) — you can unsubscribe anytime</li>
              <li>To review Fund a Family applications</li>
              <li>To track anonymous usage patterns for product improvement</li>
            </ul>
          </Section>

          <Section title="4. What We Do NOT Do">
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>We do NOT sell your data to anyone. Ever.</li>
              <li>We do NOT share your data with advertisers</li>
              <li>We do NOT use your data for targeted advertising</li>
              <li>We do NOT store credit card numbers</li>
            </ul>
          </Section>

          <Section title="5. Fund a Family Proof Documents">
            If you submit a proof document (EBT/SNAP card, Medicaid, etc.) as part of a Fund a Family application, it is reviewed only by the Bitcoin Brain founder (Bryce Fields). Proof documents are stored securely and are deleted within 30 days of application review, regardless of outcome.
          </Section>

          <Section title="6. Third-Party Services">
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li><strong className="text-white">Stripe:</strong> Payment processing (card). Subject to <a href="https://stripe.com/privacy" className="text-[#F7931A] hover:underline" target="_blank" rel="noopener noreferrer">Stripe's Privacy Policy</a>.</li>
              <li><strong className="text-white">Strike:</strong> Bitcoin/Lightning payment processing. Subject to <a href="https://strike.me/legal/privacy" className="text-[#F7931A] hover:underline" target="_blank" rel="noopener noreferrer">Strike's Privacy Policy</a>.</li>
              <li><strong className="text-white">Resend:</strong> Email delivery. Subject to <a href="https://resend.com/legal/privacy-policy" className="text-[#F7931A] hover:underline" target="_blank" rel="noopener noreferrer">Resend's Privacy Policy</a>.</li>
            </ul>
          </Section>

          <Section title="7. Data Retention and Deletion">
            Your account data is retained as long as your account exists. You can delete your account and all associated data at any time from your <strong className="text-white">Profile &gt; Danger Zone &gt; Delete Account</strong>. When you delete your account, the following happens immediately:
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>A confirmation email is sent to your email address</li>
              <li>All personal data is permanently removed — profile, course progress, quiz scores, BTC XP, achievements, badges, streaks, purchase records, game data, and email preferences</li>
              <li>Your session is terminated and you are logged out</li>
              <li>You cannot log in again with the same email unless you create a new account</li>
            </ul>
            <p className="mt-2">Deletion is immediate and irreversible. A hashed audit record (no personal data) is retained for legal compliance. You may also request deletion by emailing <a href="mailto:bitcoinbrain21m@gmail.com" className="text-[#F7931A] hover:underline">bitcoinbrain21m@gmail.com</a> — we will process your request within 30 days.</p>
          </Section>

          <Section title="8. Your Rights (GDPR / CCPA)">
            <p className="mb-2"><strong className="text-white">If you are in the EU/EEA (GDPR):</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li><strong className="text-white">Right of access:</strong> Request a copy of your personal data</li>
              <li><strong className="text-white">Right to rectification:</strong> Correct inaccurate data via your Profile settings</li>
              <li><strong className="text-white">Right to erasure:</strong> Delete your account and all data via Profile &gt; Delete Account</li>
              <li><strong className="text-white">Right to data portability:</strong> Request your data in a machine-readable format</li>
              <li><strong className="text-white">Right to object:</strong> Opt out of marketing emails via Profile &gt; Email Preferences or the unsubscribe link in any email</li>
            </ul>
            <p className="mt-3 mb-2"><strong className="text-white">If you are in California (CCPA):</strong></p>
            <ul className="list-disc pl-5 space-y-1 mt-1">
              <li>You have the right to know what personal information is collected and request its deletion</li>
              <li>You have the right to opt out of the sale of personal information — we never sell your data, so this right is automatically satisfied</li>
              <li>We will not discriminate against you for exercising your privacy rights</li>
            </ul>
            <p className="mt-2">For any data request, contact <a href="mailto:bitcoinbrain21m@gmail.com" className="text-[#F7931A] hover:underline">bitcoinbrain21m@gmail.com</a>.</p>
          </Section>

          <Section title="9. Children's Privacy">
            See our separate COPPA Notice for details on how we handle data for users under 13.
          </Section>

          <Section title="10. Changes">
            We may update this policy periodically. Material changes will be communicated via email.
          </Section>

          <Section title="11. Contact">
            Questions? <a href="mailto:bitcoinbrain21m@gmail.com" className="text-[#F7931A] hover:underline">bitcoinbrain21m@gmail.com</a>
          </Section>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="text-white font-heading font-bold text-base mb-2">{title}</h2>
      <div>{children}</div>
    </div>
  );
}
